import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Pencil, ShieldCheck, UserPlus, X } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import {
  getSalesAffiliateFunctionErrorMessage,
  salesAffiliateFunctionCodeMessage,
} from "../lib/salesAffiliateCommercial";

const LEAD_FUNCTION = "sales_affiliate_lead_registration";
const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  company: "",
  email: "",
  phone: "",
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirrors the certified backend rule:
// ^[0-9+()\-.[:space:]]{5,40}$
const PHONE_PATTERN = /^[0-9+().\-\s]{5,40}$/;

function validate(form) {
  if (!form.first_name.trim()) return "First name is required.";
  if (!form.last_name.trim()) return "Last name is required.";
  if (!form.company.trim()) return "Company is required.";
  if (!form.email.trim()) return "Email is required.";
  if (!EMAIL_PATTERN.test(form.email.trim())) return "Enter a valid email address.";

  const phone = form.phone.trim();
  if (phone && !PHONE_PATTERN.test(phone)) {
    return "Enter a valid phone number using 5–40 characters: digits, spaces, +, -, parentheses, or periods.";
  }

  return "";
}

function ReviewModal({ form, submitting, onEdit, onConfirm }) {
  const confirmRef = useRef(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-prospect-title"
    >
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 bg-gradient-to-r from-blue-50 via-white to-teal-50 px-6 py-5">
          <div className="flex gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white">
              <ShieldCheck size={21} />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
                Double-check prospect details
              </div>
              <h2
                id="review-prospect-title"
                className="mt-1 text-xl font-black text-slate-950"
              >
                Review Prospect Details
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            disabled={submitting}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close review"
          >
            <X size={19} />
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm leading-6 text-slate-600">
            Confirm the information below before registering and protecting this
            prospect.
          </p>

          <dl className="mt-5 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:grid-cols-2">
            {[
              ["First name", form.first_name],
              ["Last name", form.last_name],
              ["Company", form.company],
              ["Email", form.email],
              ["Phone", form.phone || "Not provided"],
            ].map(([label, value]) => (
              <div
                key={label}
                className={
                  label === "Company" || label === "Email"
                    ? "sm:col-span-2"
                    : ""
                }
              >
                <dt className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                  {label}
                </dt>
                <dd className="mt-1 break-words text-sm font-bold text-slate-900">
                  {value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onEdit}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <Pencil size={16} />
              Edit
            </button>
            <button
              ref={confirmRef}
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Registering…" : "Confirm Registration"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SalesAffiliateLeadRegistrationForm({ onRegistered }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [serverError, setServerError] = useState("");
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const clearMessages = () => {
    setValidationError("");
    setServerError("");
    setDuplicateMessage("");
    setSuccessMessage("");
  };

  const handleReview = (event) => {
    event.preventDefault();
    if (submitting) return;

    const error = validate(form);
    clearMessages();
    setValidationError(error);
    if (!error) setReviewing(true);
  };

  const handleConfirm = async () => {
    if (submitting) return;

    setSubmitting(true);
    setServerError("");
    setDuplicateMessage("");

    try {
      const { data, error } = await supabase.functions.invoke(LEAD_FUNCTION, {
        body: {
          action: "register",
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
        },
      });

      if (error) {
        throw new Error(
          await getSalesAffiliateFunctionErrorMessage(
            error,
            "Unable to register this prospect right now. Please try again.",
          ),
        );
      }

      if (!data?.ok) {
        throw new Error(
          salesAffiliateFunctionCodeMessage(
            data?.error,
            "Unable to register this prospect right now. Please try again.",
          ),
        );
      }

      if (data.status === "REJECTED_DUPLICATE") {
        setDuplicateMessage(
          data.message ||
            "This business is already registered with the AI Fine Protector Sales Affiliate Program and cannot be accepted as a new prospect. If you believe this is an error, please contact Sales Affiliate Support.",
        );
        setReviewing(false);

        // The certified backend persists the attempted REJECTED_DUPLICATE row.
        // Refresh so the Affiliate's visible history matches that durable record.
        if (onRegistered) await onRegistered();
        return;
      }

      setForm(EMPTY_FORM);
      setReviewing(false);
      setSuccessMessage("Prospect registered and protected successfully.");
      if (onRegistered) await onRegistered();
    } catch (err) {
      setServerError(
        err?.message || "Unable to register this prospect right now. Please try again.",
      );
      setReviewing(false);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-50";

  return (
    <section className="overflow-hidden rounded-3xl border border-blue-200 bg-white shadow-sm">
      <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 via-white to-teal-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm">
            <UserPlus size={20} />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
              + Add New Prospect
            </div>
            <h2 className="mt-0.5 text-xl font-black text-slate-950">
              Register a Prospect
            </h2>
          </div>
        </div>
      </div>

      <div className="px-6 py-5">
        <p className="text-sm leading-6 text-slate-600">
          Enter the business details and the email address the prospective
          customer is expected to use when creating their AI Fine Protector
          account and making a qualifying purchase. Global first-accepted
          protection applies, so each business may be registered only once
          across the Sales Affiliate Program.
        </p>

        <form onSubmit={handleReview} className="mt-4 space-y-3.5">
          <div className="grid gap-3.5 sm:grid-cols-2">
            <div>
              <label
                htmlFor="affiliate-prospect-first-name"
                className="text-xs font-black uppercase tracking-[0.18em] text-slate-400"
              >
                First name
              </label>
              <input
                id="affiliate-prospect-first-name"
                type="text"
                autoComplete="given-name"
                required
                value={form.first_name}
                onChange={handleChange("first_name")}
                disabled={submitting}
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="affiliate-prospect-last-name"
                className="text-xs font-black uppercase tracking-[0.18em] text-slate-400"
              >
                Last name
              </label>
              <input
                id="affiliate-prospect-last-name"
                type="text"
                autoComplete="family-name"
                required
                value={form.last_name}
                onChange={handleChange("last_name")}
                disabled={submitting}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="affiliate-prospect-company"
              className="block text-xs font-black uppercase tracking-[0.18em] text-slate-400"
            >
              Company
            </label>
            <input
              id="affiliate-prospect-company"
              type="text"
              autoComplete="organization"
              required
              value={form.company}
              onChange={handleChange("company")}
              disabled={submitting}
              className={inputClass}
            />
          </div>

          <div className="grid gap-3.5 sm:grid-cols-2">
            <div>
              <label
                htmlFor="affiliate-prospect-email"
                className="text-xs font-black uppercase tracking-[0.18em] text-slate-400"
              >
                Prospective customer account email
              </label>
              <input
                id="affiliate-prospect-email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={handleChange("email")}
                disabled={submitting}
                className={inputClass}
                aria-describedby="affiliate-prospect-email-help"
              />
              <p
                id="affiliate-prospect-email-help"
                className="mt-1.5 text-[11px] font-semibold leading-4 text-slate-500"
              >
                Use the email the prospective customer is expected to use for
                their AI Fine Protector account and purchase.
              </p>
            </div>

            <div>
              <label
                htmlFor="affiliate-prospect-phone"
                className="text-xs font-black uppercase tracking-[0.18em] text-slate-400"
              >
                Phone <span className="text-slate-400">(optional)</span>
              </label>
              <input
                id="affiliate-prospect-phone"
                type="tel"
                autoComplete="tel"
                maxLength={40}
                value={form.phone}
                onChange={handleChange("phone")}
                disabled={submitting}
                className={inputClass}
                aria-describedby="affiliate-prospect-phone-help"
              />
              <p
                id="affiliate-prospect-phone-help"
                className="mt-1.5 text-[11px] font-semibold leading-4 text-slate-500"
              >
                5–40 characters; digits, spaces, +, -, parentheses, and periods
                are accepted.
              </p>
            </div>
          </div>

          {validationError ? (
            <div
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {validationError}
            </div>
          ) : null}

          {duplicateMessage ? (
            <div
              role="alert"
              className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800"
            >
              {duplicateMessage}
            </div>
          ) : null}

          {serverError ? (
            <div
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {serverError}
            </div>
          ) : null}

          {successMessage ? (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
              <CheckCircle2 size={18} />
              {successMessage}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-black text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
          >
            Review Prospect Details
          </button>
        </form>
      </div>

      {reviewing ? (
        <ReviewModal
          form={form}
          submitting={submitting}
          onEdit={() => setReviewing(false)}
          onConfirm={handleConfirm}
        />
      ) : null}
    </section>
  );
}
